import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  userAPI,
  trainingAPI,
  appointmentAPI,
  notificationAPI,
  authAPI,
  settingsAPI,
} from '../services/api';
import {
  parseLocalDate,
  formatDateShort,
  formatTime,
  formatDateTime,
  getDayOfWeek,
  getHoursUntil,
} from '../utils/dateTimeHelpers';
import '../styles/App.css';

function ProfilePage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trainingHours, setTrainingHours] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [cancellationLockoutHours, setCancellationLockoutHours] = useState(12);

  // Fetch public settings
  const fetchPublicSettings = useCallback(async () => {
    try {
      const data = await settingsAPI.getPublicSettings();
      if (data && data.cancellation_lockout_hours) {
        setCancellationLockoutHours(data.cancellation_lockout_hours);
      }
    } catch (error) {
      console.error('Error fetching public settings:', error);
      // Use default value of 12 on error
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationAPI.getNotifications();
      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  // Fetch user appointments count
  const fetchAppointmentsCount = useCallback(async (userId) => {
    try {
      const data = await appointmentAPI.getUserAppointmentCount(userId);
      setAppointmentsCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching appointments count:', error);
    }
  }, []);

  // Fetch user appointments list
  const fetchUserAppointments = useCallback(async (userId) => {
    try {
      const data = await appointmentAPI.getUserAppointments(userId);
      setAppointments(data || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, []);

  // Fetch training hours
  const fetchTrainingHours = useCallback(async () => {
    try {
      const data = await trainingAPI.getSessions();
      setTrainingHours(data || []);
    } catch (error) {
      console.error('Error fetching training hours:', error);
    }
  }, []);

  // Fetch user data
  const fetchUserData = useCallback(async (userId) => {
    setRefreshing(true);
    try {
      const updatedUser = await userAPI.getUser(userId);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Note: Cleanup is now handled by the backend automatically
  // Regular users don't need to call the admin cleanup endpoint

  // Initial data load
  useEffect(() => {
    const fetchData = async () => {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        setUser(userData);
        await Promise.all([
          fetchUserData(userData.id),
          fetchAppointmentsCount(userData.id),
          fetchUserAppointments(userData.id),
          fetchTrainingHours(),
          fetchNotifications(),
          fetchPublicSettings(),
        ]);
      }
      setLoading(false);
    };

    fetchData();
  }, [
    fetchUserData,
    fetchAppointmentsCount,
    fetchUserAppointments,
    fetchTrainingHours,
    fetchNotifications,
    fetchPublicSettings,
  ]);

  // Auto-refresh user data every 60 seconds
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      fetchUserData(user.id);
    }, 60000);

    return () => clearInterval(interval);
  }, [user, fetchUserData]);

  // Handle appointment deletion
  const handleDeleteAppointment = async (appointment) => {
    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך למחוק את הקביעה ב${formatDateTime(
        appointment.training_date,
        appointment.training_time
      )}?`
    );

    if (!confirmed) return;

    try {
      const response = await appointmentAPI.cancelAppointment(appointment.id);

      if (response.success) {
        alert('הקביעה נמחקה בהצלחה!');
        await Promise.all([
          fetchAppointmentsCount(user.id),
          fetchTrainingHours(),
          fetchUserAppointments(user.id),
        ]);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert(error.response?.data?.error || 'שגיאה במחיקת הקביעה');
    }
  };

  // Handle booking
  const handleBooking = async (training) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Check if subscription has expired before booking
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const endDate = parseLocalDate(user.end_date);
    endDate.setHours(23, 59, 59, 999);

    if (endDate < currentDate) {
      setIsSubmitting(false);
      alert('המנוי שלך פג. לחדש מנוי נא ליצור קשר עם מלאק.');
      return;
    }

    // Check if user already has an appointment on this date
    const hasAppointmentOnSameDay = appointments.some(
      (apt) => apt.training_date === training.training_date
    );

    if (hasAppointmentOnSameDay) {
      setIsSubmitting(false);
      alert('לא ניתן לקבוע שני אימונים באותו יום');
      return;
    }

    const confirmed = window.confirm(
      `האם אתה בטוח שברצונך לקבוע תור לאימון ב${formatDateTime(
        training.training_date,
        training.training_time
      )}?`
    );

    if (!confirmed) {
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await appointmentAPI.bookAppointment(
        training.training_date,
        training.training_time
      );

      if (response.success) {
        alert('הקביעה בוצעה בהצלחה!');
        await Promise.all([
          fetchTrainingHours(),
          fetchAppointmentsCount(user.id),
          fetchUserAppointments(user.id),
        ]);
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert(error.response?.data?.error || 'שגיאה בקביעת התור');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    authAPI.logout();
    window.location.href = '/';
  };

  // Check if all training days are booked
  const isAllTrainingDaysBooked = () => {
    return Number(appointmentsCount) >= Number(user.training_days);
  };

  // Group trainings by date
  const groupByDate = () => {
    const now = new Date();

    const upcomingTrainings = trainingHours.filter((training) => {
      const trainingDate = new Date(`${training.training_date}T${training.training_time}`);
      return trainingDate >= now;
    });

    const grouped = upcomingTrainings.reduce((acc, training) => {
      const trainingDate = parseLocalDate(training.training_date);
      const trainingTime = training.training_time;
      const fullDate = new Date(`${training.training_date}T${trainingTime}`);
      const dateString = formatDateShort(training.training_date);

      const existingGroup = acc.find((group) => group.date === dateString);
      if (existingGroup) {
        existingGroup.trainings.push({ ...training, fullDate });
      } else {
        acc.push({
          date: dateString,
          fullDate: trainingDate,
          trainings: [{ ...training, fullDate }],
        });
      }

      return acc;
    }, []);

    return grouped.sort((a, b) => a.fullDate - b.fullDate).map((group) => {
      group.trainings.sort((a, b) => a.fullDate - b.fullDate);
      return group;
    });
  };

  const groupedTrainingHours = groupByDate();

  // Toggle table visibility
  const toggleTableVisibility = () => {
    setIsTableVisible((prev) => !prev);
  };

  const handleOutsideClick = (e) => {
    if (e.target.className === 'overlay') {
      setIsTableVisible(false);
    }
  };

  const handleCloseTable = () => {
    setIsTableVisible(false);
  };

  // Toggle notifications popup
  const togglePopup = () => {
    setIsPopupVisible((prev) => !prev);
  };

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>טוען...</p>
      </div>
    );
  }

  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/" />;
  }

  // Check if subscription has expired
  // Compare dates only (ignore time component)
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);

  const endDate = parseLocalDate(user.end_date);
  endDate.setHours(23, 59, 59, 999); // Subscription valid until end of the day

  if (endDate < currentDate) {
    // Subscription has expired - logout and redirect to login
    authAPI.logout();
    return <Navigate to="/" />;
  }

  return (
    <div className="Profile-page">
      <div className="hero-section">
        <div className="profile-header">
          <button className="logout-button" onClick={handleLogout}>
            <img
              src="https://malak.azappcraft.com/wp-content/uploads/2024/11/logout.png"
              alt="Logout"
              className="logout-icon"
            />
          </button>
          <div className="notifications-section">
            <button className="notifications-button" onClick={togglePopup}>
              🔔 התראות
            </button>

            {isPopupVisible && (
              <div className="notifications-popup">
                <div className="popup-header">
                  <h3>התראות</h3>
                  <button className="close-button" onClick={togglePopup}>
                    X
                  </button>
                </div>
                <div className="popup-content">
                  {notifications.length > 0 ? (
                    <ul className="notifications-list">
                      {notifications.map((notification, index) => (
                        <li key={index} className="notification-item">
                          <p className="notification-text">
                            {notification.notification_text}
                          </p>
                          <span className="notification-date">
                            {new Date(notification.created_at).toLocaleString('he-IL')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-notifications">אין התראות חדשות</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <h1 className="greeting">היי {user.full_name}</h1>
        <div className="user-info">
          <p className="user-params">
            <strong>מנוי עד</strong> <br /> {formatDateShort(user.end_date)}
          </p>
          <p className="user-params">
            <strong>סוג</strong> <br />
            {Array.isArray(user.training_type)
              ? user.training_type.join(' & ')
              : user.training_type}
          </p>
          <p className="user-params">
            <strong>מותר לקבוע (ימים)</strong> <br /> {user.training_days}
          </p>
          <p className="user-params">
            <strong>ימים נקבעו</strong> <br /> {appointmentsCount}
          </p>
        </div>
        <div className="my-appointments-section">
          <button className="my-appointments-btn" onClick={toggleTableVisibility}>
            &#x1F4C5; האימונים שלי
          </button>
        </div>
      </div>

      {isTableVisible && <div className="overlay" onClick={handleOutsideClick}></div>}

      {isTableVisible && (
        <div className="appointments-section">
          <div className="close-button">
            <button onClick={handleCloseTable}>X</button>
          </div>
          <table>
            <thead>
              <tr>
                <th>תאריך</th>
                <th>שעה</th>
                <th>סוג אימון</th>
                <th>מחיקה</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((appointment, index) => {
                const hoursLeft = getHoursUntil(appointment.training_date, appointment.training_time);
                const isDeleteDisabled = hoursLeft < cancellationLockoutHours;

                return (
                  <tr
                    key={`${appointment.training_date}-${appointment.training_time}-${index}`}
                  >
                    <td>{formatDateShort(appointment.training_date)}</td>
                    <td>{formatTime(appointment.training_time)}</td>
                    <td>{appointment.training_type || '-'}</td>
                    <td>
                      <button
                        onClick={() => handleDeleteAppointment(appointment)}
                        disabled={isDeleteDisabled}
                        style={{
                          cursor: isDeleteDisabled ? 'not-allowed' : 'pointer',
                          backgroundColor: isDeleteDisabled ? '#d3d3d3' : '#d96060',
                          color: isDeleteDisabled ? '#a1a1a1' : 'white',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '5px',
                        }}
                        title={isDeleteDisabled ? `לא ניתן לבטל פחות מ-${cancellationLockoutHours} שעות לפני האימון` : 'לחץ כדי לבטל את התור'}
                      >
                        מחק
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {refreshing && <p>מרענן נתוני משתמש...</p>}

      {isAllTrainingDaysBooked() ? (
        <div className="no-more-appointments">
          <p>.הגעת למכסה הקביעות שלך. לא ניתן לקבוע יותר אימונים</p>
        </div>
      ) : (
        <div className="Training-hours">
          <div className="dates-slider">
            {groupedTrainingHours
              .map((group) => {
                // Filter out past trainings from each group
                const now = new Date();
                const upcomingTrainings = group.trainings.filter(
                  (training) => training.fullDate >= now
                );
                return { ...group, trainings: upcomingTrainings };
              })
              .filter((group) => group.trainings.length > 0) // Hide date groups with no upcoming trainings
              .map((group) => {
              const dayOfWeek = getDayOfWeek(group.fullDate);
              return (
                <div key={group.date} className="date-item">
                  <div className="date-header">
                    <h4 className="day-view">{dayOfWeek}</h4>
                    <h5 className="date-view">{group.date}</h5>
                  </div>
                  <div className="hours-list">
                    {group.trainings.map((training) => {
                      const isTrainingTypeMatch =
                        training.training_type === user.training_type ||
                        (['בר', 'מכשיר'].includes(training.training_type) &&
                          ['בר', 'מכשיר'].includes(user.training_type));

                      const isAfterEndDate =
                        parseLocalDate(training.training_date) > parseLocalDate(user.end_date);
                      const isFullyBooked =
                        Number(training.current_participants) >=
                        Number(training.max_participants);

                      let buttonClass = 'training-button';
                      let buttonText = '';

                      if (isAfterEndDate) {
                        buttonClass += ' expired';
                        buttonText = 'לא זמין';
                      } else if (isFullyBooked && !isTrainingTypeMatch) {
                        buttonClass += ' disabled';
                        buttonText = `${training.training_type} (${training.training_time.slice(
                          0,
                          5
                        )})`;
                      } else if (isFullyBooked) {
                        buttonClass += ' unavailable';
                        buttonText = 'מלא';
                      } else if (isTrainingTypeMatch) {
                        buttonClass += ' available';
                        buttonText = `${training.training_type} (${formatTime(training.training_time)})`;
                      } else {
                        buttonClass += ' disabled';
                        buttonText = `${training.training_type} (${formatTime(training.training_time)})`;
                      }

                      return (
                        <button
                          key={training.id || `${training.training_date}-${training.training_time}`}
                          className={buttonClass}
                          disabled={
                            isAfterEndDate ||
                            isFullyBooked ||
                            !isTrainingTypeMatch ||
                            user.training_days <= 0
                          }
                          onClick={() => handleBooking(training)}
                        >
                          {buttonText}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
